import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Instagram, Eye, Check } from "lucide-react";
import { contactInfo } from "../data/mockData";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { openWhatsApp } from "../lib/whatsapp";
import GradientText from "./GradientText";
import TextType from "./TextType";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const navigate = useNavigate();

  const handleWhatsApp = () => {
    openWhatsApp("Hola, vengo de la página web y me gustaría hacer una consulta sobre un servicio.");
  };

  const handleInstagram = () => {
    window.open("https://www.instagram.com/m/rgserviciotec", "_blank");
  };

  const goToPlans = () => {
    navigate("/servicios#planes");
  };

  return (
    <section className="hero-section relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f2f7ff] via-white to-[#e6f4ff] dark:from-[#05070d] dark:via-[#0b1220] dark:to-[#02050c] pb-20 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="text-center max-w-4xl mx-auto lg:max-w-none">
          <div className="flex justify-center mb-4 animate-slide-up">
            <span className="text-xs sm:text-sm uppercase tracking-[0.35em] text-gray-600 dark:text-gray-100">
              Diagnóstico inmediato y asesoría experta
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-[1.15] animate-slide-up pt-6 pb-1 flex flex-wrap items-baseline justify-center gap-x-3">
            <span className="w-full sm:w-auto">Hablemos de lo que necesita</span>
            <span className="w-full sm:w-auto">
              <span className="mr-2">tu</span>
              <GradientText
                className="inline-flex items-baseline px-1 py-1"
                colors={["#2563eb", "#06b6d4", "#22d3ee"]}
                animationSpeed={6}
              >
                equipo
              </GradientText>
            </span>
          </h1>

          {/* Subtitle */}
          <div className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed animate-slide-up animation-delay-200 min-h-[4.5rem] sm:min-h-[3.75rem] flex items-center justify-center">
            <TextType
              text={[
                "Especialistas en reparación de PC, notebooks, consolas y celulares.",
                "Diagnóstico interactivo con recomendaciones claras y rápidas.",
                "Servicio técnico profesional con garantía y trato personalizado."
              ]}
              typingSpeed={35}
              deletingSpeed={20}
              pauseDuration={2200}
              showCursor={false}
              className="text-lg sm:text-xl"
            />
          </div>

          {/* Action Buttons & Features Container */}
          <div className="flex flex-col items-center w-full animate-slide-up animation-delay-400">
            {/* Buttons Container */}
            <div className="w-full flex flex-col items-center sm:flex-row sm:justify-center sm:gap-4">
              <Button
                onClick={handleWhatsApp}
                size="lg"
                aria-label="Contactar por WhatsApp"
                className="plan-button plan-no-outline w-full max-w-sm h-12 mb-4 sm:w-auto sm:max-w-none sm:mb-0 bg-gradient-to-r from-emerald-500 to-green-600 text-white px-8 py-4 text-base font-semibold shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 dark:text-white"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Escribinos por WhatsApp
              </Button>

              <Button
                onClick={handleInstagram}
                size="lg"
                variant="outline"
                aria-label="Ver perfil de Instagram"
                className="plan-button plan-no-outline w-full max-w-sm h-12 mb-4 sm:w-auto sm:max-w-none sm:mb-0 bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white hover:text-white dark:text-white px-8 py-4 text-base font-semibold shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Instagram className="h-5 w-5" />
                Escribinos por Instagram
              </Button>

              <Button
                onClick={goToPlans}
                size="lg"
                variant="outline"
                aria-label="Ir a la sección de planes"
                className="plan-button plan-outline-hover w-full max-w-sm h-12 sm:w-auto sm:max-w-none text-black dark:text-black bg-white dark:bg-white hover:bg-gray-200 dark:hover:bg-gray-200 hover:text-black px-8 py-4 text-base font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Eye className="h-5 w-5" />
                Ver Planes
              </Button>
            </div>

            {/* Benefits Container */}
            <div className="hidden sm:flex flex-col sm:flex-row items-center sm:justify-center gap-4 sm:gap-8 text-gray-600 dark:text-gray-400 mt-6 animate-slide-up animation-delay-600">
              <div className="flex items-center gap-2 group">
                <Check className="h-5 w-5 text-green-500 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-lg leading-relaxed">Garantía incluida</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-gray-300 dark:bg-gray-700"></div>
              <div className="flex items-center gap-2 group">
                <Check className="h-5 w-5 text-green-500 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-lg leading-relaxed">Trato personalizado</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-gray-300 dark:bg-gray-700"></div>
              <div className="flex items-center gap-2 group">
                <Check className="h-5 w-5 text-green-500 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium text-lg leading-relaxed">Todas las marcas</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
